import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';
import { useAppStore } from '../store/appStore';

import { Button } from './Button';

export interface HeaderPiiConfirmationCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

/**
 * v0.6 Stage B6 — UI card for the ``header_pii_confirmation`` HITL.
 *
 * A single consolidated card listing every column whose header matched
 * the post-FIA PII keyword scan (``agents/pii.py::_header_pii_scan``).
 * The reviewer un-ticks any column they confirm is not sensitive; the
 * subset left ticked is what stays blurred in the upload preview.
 *
 * Distinct from the per-column PII output-strategy HITLs (the
 * drop/mask/clean radio picker rendered by ``HitlQueue``) — those are a
 * downstream decision about what to do with PII in the cleansed output.
 * This card is *only* about the upload-preview blur.
 *
 * Carry-forward keys on the file's SHA-256 (``last_header_pii_confirmation_for``),
 * so same-bytes re-uploads inherit the prior selection without re-prompting.
 *
 * Scope-boundary note: detection is header-name only. A column named
 * ``field_4`` carrying real names won't be flagged here — the downstream
 * content-based PII detector is the safety net for non-descriptive
 * headers. The card surfaces this limit explicitly.
 */
export function HeaderPiiConfirmationCard({
  hit,
  onAction,
  reviewerId,
}: HeaderPiiConfirmationCardProps) {
  const columns: string[] =
    Array.isArray(hit.options) ? hit.options.filter((c): c is string => typeof c === 'string') : [];

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  // All columns are pre-checked = "keep blurred". Reviewer unchecks
  // columns they confirm are NOT sensitive.
  const [checked, setChecked] = useState<Set<string>>(() => new Set(columns));

  const toggle = (col: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
      } else {
        next.add(col);
      }
      return next;
    });
  };

  const onConfirm = async () => {
    setPending('confirm_header_pii');
    setError(null);
    try {
      // Preserve display order; the audit payload reads cleaner when the
      // columns appear in the same order the reviewer saw them.
      const kept = columns.filter((c) => checked.has(c));
      await onAction(hit.hit_id, {
        type: 'confirm_header_pii',
        payload: { columns: kept },
        reviewer_id: reviewerId,
      });
      // v0.6 Stage B8 (I1) — patch the run's ``header_pii_columns`` in
      // the local store to the kept subset immediately. The backend
      // re-kick (``_phase_header_pii_scan`` reading the carry-forward)
      // will produce the same list on the next pipeline pass, but
      // patching now means TablePreview re-renders without the
      // unchecked columns the moment confirm completes — no need to
      // wait for the pipeline to round-trip before the blur lifts.
      useAppStore.getState().updateRun(hit.run_id, {
        header_pii_columns: kept,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm');
    } finally {
      setPending(null);
    }
  };

  if (resolved) {
    return (
      <article
        className="rounded-md border border-border bg-surface-alt/40 p-3"
        data-testid="header-pii-confirmation-card-resolved"
      >
        <p className="text-sm text-muted m-0">
          Header PII selection confirmed.
        </p>
      </article>
    );
  }

  return (
    <article
      className="rounded-md border border-border bg-surface p-3 space-y-3"
      data-testid="header-pii-confirmation-card"
      aria-labelledby={`${hit.hit_id}-header-pii-title`}
    >
      <header className="space-y-1">
        <h3
          id={`${hit.hit_id}-header-pii-title`}
          className="m-0 text-sm font-semibold"
        >
          Confirm columns to blur
        </h3>
        <p className="m-0 text-sm text-muted">
          We&apos;ve blurred the following columns as they may contain
          sensitive information. Select which should remain blurred while
          you work.
        </p>
      </header>

      <fieldset
        className="rounded border border-border/60 bg-surface-alt/30 px-3 py-2"
        disabled={pending !== null}
      >
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
          Detected columns
        </legend>
        <ul className="space-y-1 pt-1">
          {columns.map((col) => {
            const inputId = `${hit.hit_id}-hpii-${col}`;
            return (
              <li key={col} className="flex items-center gap-2">
                <input
                  id={inputId}
                  type="checkbox"
                  checked={checked.has(col)}
                  onChange={() => toggle(col)}
                  className="accent-brand-dark"
                  data-testid={`header-pii-checkbox-${col}`}
                />
                <label
                  htmlFor={inputId}
                  className="font-mono text-sm cursor-pointer"
                >
                  {col}
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>

      <p className="text-xs text-muted m-0 italic">
        Detection is based on column names. Columns with non-descriptive
        names may not be shown here.
      </p>

      {error && <p className="text-xs text-danger m-0">{error}</p>}

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          onClick={() => void onConfirm()}
          disabled={pending !== null}
          data-testid="header-pii-confirm"
        >
          {pending === 'confirm_header_pii' ? 'Confirming…' : 'Confirm'}
        </Button>
      </div>
    </article>
  );
}
